import React, { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useTranslation } from "react-i18next";
import { changeResultsFilters } from "../searchFlightsSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { format } from "date-fns";

const timeMin = 0;
const timeMax = 1439;
const defaultColor = "#3273dc";

function TimeDropdown() {
  const dropdownRef = useRef(null);
  const [active, setActive] = useState<boolean>(false);
  useOnClickOutside(
    dropdownRef,
    useCallback(() => setActive(false), [setActive])
  );
  const [depart, setDepart] = useState<number[]>([timeMin, timeMax]);
  const [arrive, setArrive] = useState<number[]>([timeMin, timeMax]);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const timeFormat = useAppSelector(
    (state) => state.config.components.timeFormat
  );

  const onClick = () => setActive(!active);

  const onDepartChange = (value) => {
    setDepart(value);
  };

  const onDepartChangeComplete = (time) => {
    dispatch(
      changeResultsFilters({
        departureTime: {
          start: time[0],
          end: time[1],
        },
      })
    );
  };

  const onArriveChange = (value) => {
    setArrive(value);
  };

  const onArriveChangeComplete = (value) => {
    dispatch(
      changeResultsFilters({
        arrivalTime: {
          start: value[0],
          end: value[1],
        },
      })
    );
  };

  // Handle Escape key to close dropdown
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && active) {
        setActive(false);
      }
    },
    [active]
  );

  // Attach keydown listener when dropdown is active
  React.useEffect(() => {
    if (active) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [active, handleKeyDown]);

  return (
    <>
      <div className={`dropdown ${active ? " is-active" : ""}`}>
        <div className="dropdown-trigger">
          <button
            className="button"
            aria-haspopup="true"
            aria-controls="dropdown-menu"
            onClick={onClick}
          >
            <span>{t("HEADER_SORT_TIME")}</span>
            <span className="icon">
              <FontAwesomeIcon icon={faAngleDown} aria-hidden="true" />
            </span>
          </button>
        </div>
        <div
          ref={dropdownRef}
          className="dropdown-menu"
          id="dropdown-menu"
          role="menu"
        >
          <div className="dropdown-content">
            <div id="time-dropdown-item" className="dropdown-item">
              {t("LABEL_SORT_BY_TIME_DEPART")}
              <div className="columns is-mobile">
                <div className="column is-narrow">
                  {formatNumber(depart[0], timeFormat)}
                </div>
                <div className="column">
                  <Slider
                    range
                    min={timeMin}
                    max={timeMax}
                    value={depart}
                    onChange={onDepartChange}
                    onChangeComplete={onDepartChangeComplete}
                    styles={{
                      track: { backgroundColor: defaultColor },
                    }}
                    keyboard
                  />
                </div>
                <div className="column is-narrow">
                  {formatNumber(depart[1], timeFormat)}
                </div>
              </div>
              {t("LABEL_SORT_BY_TIME_ARRIVE")}
              <div className="columns is-mobile">
                <div className="column mobile is-narrow">
                  {formatNumber(arrive[0], timeFormat)}
                </div>
                <div className="column">
                  <Slider
                    range
                    min={timeMin}
                    max={timeMax}
                    value={arrive}
                    onChange={onArriveChange}
                    onChangeComplete={onArriveChangeComplete}
                    styles={{
                      track: { backgroundColor: defaultColor },
                    }}
                    keyboard
                  />
                </div>
                <div className="column is-narrow">
                  {formatNumber(arrive[1], timeFormat)}
                </div>
              </div>
            </div>
          </div>
        </div>
        &nbsp;
      </div>
    </>
  );
}

function formatNumber(number: number, timeFormat: string): string {
  const hours = Math.floor(number / 60);
  const minutes = Math.round(number % 60);
  const date = new Date(2021, 1, 1, hours, minutes);
  return format(date, timeFormat);
}

export default TimeDropdown;
